'use client'

import { useState } from 'react'
import { ChatBubbleOvalLeftEllipsisIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/classnames'
import { pushEvent } from '@/lib/gtm'

type FaqBlock = { type: 'p' | 'ol' | 'ul' | 'notes'; keys: string[] }

const ITEMS: { id: string; question: string; blocks: FaqBlock[] }[] = [
  {
    id: 'device',
    question: 'Landing.Faq.Device.Question',
    blocks: [{ type: 'p', keys: ['Landing.Faq.Device.P1', 'Landing.Faq.Device.P2', 'Landing.Faq.Device.P3'] }],
  },
  {
    id: 'install',
    question: 'Landing.Faq.Install.Question',
    blocks: [{ type: 'p', keys: ['Landing.Faq.Install.P1', 'Landing.Faq.Install.P2', 'Landing.Faq.Install.P3'] }],
  },
  {
    id: 'activate',
    question: 'Landing.Faq.Activate.Question',
    blocks: [
      { type: 'p', keys: ['Landing.Faq.Activate.P1', 'Landing.Faq.Activate.P2'] },
      { type: 'ol', keys: ['Landing.Faq.Activate.S1', 'Landing.Faq.Activate.S2', 'Landing.Faq.Activate.S3'] },
      { type: 'notes', keys: ['Landing.Faq.Activate.N1', 'Landing.Faq.Activate.N2'] },
      { type: 'p', keys: ['Landing.Faq.Activate.P3'] },
    ],
  },
  {
    id: 'validity',
    question: 'Landing.Faq.Validity.Question',
    blocks: [{ type: 'p', keys: ['Landing.Faq.Validity.P1', 'Landing.Faq.Validity.P2'] }],
  },
  {
    id: 'topup',
    question: 'Landing.Faq.Topup.Question',
    blocks: [{ type: 'p', keys: ['Landing.Faq.Topup.P1', 'Landing.Faq.Topup.P2', 'Landing.Faq.Topup.P3', 'Landing.Faq.Topup.P4'] }],
  },
  {
    id: 'number',
    question: 'Landing.Faq.Number.Question',
    blocks: [{ type: 'p', keys: ['Landing.Faq.Number.P1', 'Landing.Faq.Number.P2', 'Landing.Faq.Number.P3'] }],
  },
  {
    id: 'data',
    question: 'Landing.Faq.Data.Question',
    blocks: [
      { type: 'p', keys: ['Landing.Faq.Data.P1'] },
      { type: 'ul', keys: ['Landing.Faq.Data.B1', 'Landing.Faq.Data.B2'] },
      { type: 'p', keys: ['Landing.Faq.Data.P2'] },
    ],
  },
  {
    id: 'payment',
    question: 'Landing.Faq.Payment.Question',
    blocks: [{ type: 'p', keys: ['Landing.Faq.Payment.P1'] }],
  },
  {
    id: 'pay-later',
    question: 'Landing.Faq.PayLater.Question',
    blocks: [{ type: 'p', keys: ['Landing.Faq.Payment.P2'] }],
  },
  {
    id: 'refund',
    question: 'Landing.Faq.Refund.Question',
    blocks: [{ type: 'p', keys: ['Landing.Faq.Refund.P1', 'Landing.Faq.Refund.P2', 'Landing.Faq.Refund.P3'] }],
  },
]

// React 18 only accepts `inert` as a string, while its types declare a boolean.
const INERT_PROPS = { inert: '' } as object

const Faq = () => {
  const { t } = useTranslation()
  const [openId, setOpenId] = useState<string | null>(ITEMS[0].id)
  const helpHref = `https://wa.me/38761603225?text=${encodeURIComponent(t('Landing.Faq.Help.WhatsAppMessage'))}`
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: ITEMS.map(item => ({
      '@type': 'Question',
      name: t(item.question),
      acceptedAnswer: { '@type': 'Answer', text: item.blocks.flatMap(block => block.keys.map(key => t(key))).join(' ') },
    })),
  }

  return (
    <section className='bg-white py-8' aria-labelledby='faq-heading' data-track-section='faq'>
      <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData).replace(/</g, '\\u003c') }} />
      <div className='mx-auto max-w-3xl px-4'>
        <div className='text-left md:text-center'>
          <p className='text-[13px] font-semibold uppercase tracking-[0.16em] text-[#F3603C]'>{t('Landing.Faq.Eyebrow')}</p>
          <h2 id='faq-heading' className='mt-3 scroll-mt-24 text-3xl font-bold tracking-tight text-blue-950'>
            {t('Landing.Faq.Heading')}
          </h2>
          <p className='mt-4 text-lg leading-relaxed text-slate-500'>{t('Landing.Faq.Description')}</p>
        </div>

        <div className='mt-6 divide-y divide-slate-200 border-y border-slate-200'>
          {ITEMS.map(item => {
            const open = openId === item.id
            const panelId = `faq-panel-${item.id}`
            const buttonId = `faq-button-${item.id}`

            return (
              <div key={item.id}>
                <h3>
                  <button
                    id={buttonId}
                    type='button'
                    aria-expanded={open}
                    aria-controls={panelId}
                    onClick={() => {
                      // `item.id` is the same in every language, so answers group under one key in GA.
                      if (!open) pushEvent('faq_expand', { question: item.id })
                      setOpenId(open ? null : item.id)
                    }}
                    className='flex w-full items-center justify-between gap-4 py-5 text-left'
                  >
                    <span className={cn('text-base font-semibold transition-colors md:text-lg', open ? 'text-[#F3603C]' : 'text-blue-950')}>{t(item.question)}</span>
                    <ChevronDownIcon className={cn('size-5 shrink-0 text-slate-400 transition-transform duration-200', open && 'rotate-180')} aria-hidden />
                  </button>
                </h3>
                <div id={panelId} role='region' aria-labelledby={buttonId} aria-hidden={!open} {...(!open && INERT_PROPS)} className={cn('grid transition-[grid-template-rows] duration-200', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
                  <div className='overflow-hidden'>
                    <div className='space-y-3 pb-5 text-[15px] leading-relaxed text-slate-600'>
                      {item.blocks.map(block => {
                        if (block.type === 'ol') {
                          return (
                            <ol key={block.keys[0]} className='list-decimal space-y-1.5 pl-5'>
                              {block.keys.map(key => (
                                <li key={key}>{t(key)}</li>
                              ))}
                            </ol>
                          )
                        }
                        if (block.type === 'ul') {
                          return (
                            <ul key={block.keys[0]} className='list-disc space-y-1.5 pl-5'>
                              {block.keys.map(key => (
                                <li key={key}>{t(key)}</li>
                              ))}
                            </ul>
                          )
                        }
                        if (block.type === 'notes') {
                          return (
                            <ul key={block.keys[0]} className='space-y-1 text-slate-500'>
                              {block.keys.map(key => (
                                <li key={key}>{t(key)}</li>
                              ))}
                            </ul>
                          )
                        }
                        return block.keys.map(key => <p key={key}>{t(key)}</p>)
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className='mt-6 flex flex-col gap-4 rounded-2xl bg-slate-50 p-5 sm:flex-row sm:items-center sm:justify-between'>
          <div className='min-w-0'>
            <h3 className='text-lg font-bold text-blue-950'>{t('Landing.Faq.Help.Heading')}</h3>
            <p className='mt-1 text-[15px] leading-relaxed text-slate-500'>{t('Landing.Faq.Help.Description')}</p>
          </div>
          <a
            href={helpHref}
            data-track-cta='whatsapp_faq'
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex min-h-12 shrink-0 items-center justify-center gap-2 rounded-xl bg-[#0F7A40] px-5 text-base font-bold text-white transition-colors hover:bg-[#0c6535]'
          >
            <ChatBubbleOvalLeftEllipsisIcon className='size-5' aria-hidden />
            {t('Landing.Agencies.CtaAction')}
          </a>
        </div>
      </div>
    </section>
  )
}

export default Faq
